/** Admin · spend & model cost (GET /admin/costs) — the variable cost of running the platform. */
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { egressCostInr } from "@talenthop/shared";
import { api } from "../../lib/api.js";
import { inrCompact, usdCompact, USD_PER_INR } from "../../lib/format.js";
import { Info } from "../../components/icons.js";
import { TrendBars } from "./adminUi.js";

interface CostCategory {
  category: string;
  amountInr: number;
  /** How the figure was arrived at — the page is not allowed to blur these together. */
  basis: "metered" | "modelled" | "unknown";
  entries: number;
}

interface AdminCosts {
  totalMtdInr: number;
  meteredInr: number;
  modelledInr: number;
  byCategory: CostCategory[];
  byProvider: { provider: string; amountInr: number }[];
  dailyTrend: { day: string; amountInr: number }[];
  perVerified: { actualInr: number; verifiedCount: number };
}

interface CostsResponse {
  costs: AdminCosts;
}

/** The only two categories that are real. `asr` and `liveness` were fiction and are gone: speech
 *  is the browser's Web Speech API and ID verification runs in sim — neither ever cost a rupee. */
const CATEGORY_META: Record<string, { label: string; color: string }> = {
  modelAi: { label: "Model / AI · Gemini", color: "var(--violet)" },
  storage: { label: "Recording storage · Azure Blob", color: "var(--teal)" },
};

function meta(category: string): { label: string; color: string } {
  return CATEGORY_META[category] ?? { label: category.replace(/_/g, " "), color: "var(--ink-mute)" };
}

/**
 * Real metered spend is genuinely sub-rupee — a résumé scan is about ₹0.05 — and the shared
 * inr()/usd() helpers round to whole units, which would render this entire page as ₹0 · $0.
 * Below ₹100 we show the actual paise; above it the shared compact formatters take over.
 */
function inrFine(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 100) return inrCompact(n);
  return `₹${n.toFixed(abs >= 1 ? 2 : 4)}`;
}

function usdFine(n: number): string {
  const dollars = n * USD_PER_INR;
  if (Math.abs(dollars) >= 1) return usdCompact(n);
  return `$${dollars.toFixed(4)}`;
}

/** Standing requirement: every money figure carries both currencies. */
function dual(n: number): string {
  return `${inrFine(n)} · ${usdFine(n)}`;
}

/** A typical interview recording — the size the storage and egress figures are quoted against. */
const TYPICAL_RECORDING_BYTES = 150 * 1024 * 1024;

export function Spend(): ReactNode {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-costs"],
    queryFn: () => api.get<CostsResponse>("/admin/costs"),
  });
  const costs = data?.costs;

  const total = costs?.totalMtdInr ?? 0;
  const metered = costs?.meteredInr ?? 0;
  const modelled = costs?.modelledInr ?? 0;
  const maxProvider = costs ? Math.max(1, ...costs.byProvider.map((p) => p.amountInr)) : 1;
  const perPlayback = egressCostInr(TYPICAL_RECORDING_BYTES);

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Spend &amp; model cost</h1>
          <div className="sub">
            Variable cost of running the platform — AI tokens we counted, recording storage we
            modelled. Azure infrastructure is not in here.
          </div>
        </div>
        <span className="pill n">MTD · platform-wide</span>
      </div>

      {isLoading && <div className="card pad mut sm">Loading spend…</div>}

      {isError && (
        <div className="banner crit">
          <span>Couldn't load spend. Please retry in a moment.</span>
        </div>
      )}

      {costs && (
        <>
          <div className="grid g4" style={{ marginBottom: 16 }}>
            <div className="card pad stat">
              <div className="l">Variable spend · MTD</div>
              <div className="n tnum">{inrFine(total)}</div>
              <div className="d">{usdFine(total)} · excludes fixed infra</div>
            </div>
            <div className="card pad stat hl">
              <div className="l">
                Model spend <span className="tag">metered</span>
              </div>
              <div className="n violet tnum">{inrFine(metered)}</div>
              <div className="d">{usdFine(metered)} · counted tokens × list rates</div>
            </div>
            <div className="card pad stat">
              <div className="l">
                Recording storage <span className="tag">modelled</span>
              </div>
              <div className="n teal tnum">{inrFine(modelled)}</div>
              <div className="d">{usdFine(modelled)} · bytes × retention × list rate</div>
            </div>
            <div className="card pad stat">
              <div className="l">Per verified candidate</div>
              <div className="n tnum">{inrFine(costs.perVerified.actualInr)}</div>
              <div className="d">
                {usdFine(costs.perVerified.actualInr)} · over {costs.perVerified.verifiedCount}{" "}
                verified
              </div>
            </div>
          </div>

          <div className="card pad" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 650, marginBottom: 12 }}>
              Monthly spend split · {dual(total)}
            </div>
            {costs.byCategory.length === 0 && (
              <p className="mut sm" style={{ margin: 0 }}>
                No spend recorded yet this month.
              </p>
            )}
            {costs.byCategory.length > 0 && (
              <>
                <div className="costbar tall">
                  {costs.byCategory.map((c) => {
                    const m = meta(c.category);
                    const pct = total > 0 ? (c.amountInr / total) * 100 : 0;
                    return (
                      <span
                        key={c.category}
                        style={{ width: `${pct}%`, background: m.color }}
                        title={`${m.label} · ${dual(c.amountInr)} · ${c.basis} · ${c.entries} ledger rows`}
                      >
                        {pct > 14 ? inrFine(c.amountInr) : ""}
                      </span>
                    );
                  })}
                </div>
                <div className="leg">
                  {costs.byCategory.map((c) => {
                    const m = meta(c.category);
                    return (
                      <span key={c.category} className={c.basis === "metered" ? "li hl" : "li"}>
                        <span className="sw" style={{ background: m.color }} />
                        {m.label} {inrFine(c.amountInr)} · {c.basis} · {c.entries} rows
                      </span>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <div className="grid g2" style={{ marginBottom: 16 }}>
            <div className="card">
              <div className="card-h">
                <h3>Daily spend · this month</h3>
                <span className="tag">₹ / day</span>
              </div>
              <div className="card-b">
                {costs.dailyTrend.length === 0 && (
                  <p className="mut sm" style={{ margin: 0 }}>
                    No daily buckets yet.
                  </p>
                )}
                {costs.dailyTrend.length > 0 && (
                  <TrendBars
                    bars={costs.dailyTrend.map((d) => ({
                      label: `${new Date(d.day).toLocaleDateString()} · ${dual(d.amountInr)}`,
                      value: d.amountInr,
                    }))}
                  />
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-h">
                <h3>Top cost drivers</h3>
                <span className="tag">By provider</span>
              </div>
              <div className="card-b">
                {costs.byProvider.length === 0 && (
                  <p className="mut sm" style={{ margin: 0 }}>
                    No provider spend yet.
                  </p>
                )}
                {costs.byProvider.map((p) => (
                  <div key={p.provider} className="meter" style={{ marginBottom: 8 }}>
                    <span className="sm mut" style={{ width: 140, flex: "none", textTransform: "capitalize" }}>
                      {p.provider.replace(/_/g, " ")}
                    </span>
                    <div className="bar">
                      <i style={{ width: `${(p.amountInr / maxProvider) * 100}%`, background: "var(--lime-strong)" }} />
                    </div>
                    <span className="val tnum" style={{ width: 78 }}>
                      {inrFine(p.amountInr)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="banner info" style={{ marginBottom: 16 }}>
            <Info />
            <div>
              <b>AI is not the line that grows with volume.</b> A résumé scan costs us about ₹0.05
              and a whole interview about ₹0.18 in tokens — a fraction of a rupee per candidate. The
              cost that actually scales is the <b>video</b>: ~150 MB per interview held for 30 days,
              plus roughly {inrFine(perPlayback)} of egress every time a recruiter plays one back
              (not metered — nothing observes a play). Everything else is fixed infrastructure.
            </div>
          </div>
        </>
      )}

      <div className="disclaimer">
        <b>Metered ≠ invoiced.</b> Model spend is <b>measured</b>: every Gemini response's real token
        counts, priced at the published per-million rates. Recording storage is <b>modelled</b>: exact
        blob bytes × exact retention window × Azure's list rate. Neither is a supplier invoice, and
        no Azure invoice is imported anywhere. <b>Infrastructure is not included here at all</b> —
        Functions, Container Apps, Postgres and email are a fixed monthly bill that no request can be
        charged for, so treat the per-candidate figure as a floor, not a loaded unit cost. USD is
        display-only at a fixed rate.
      </div>
    </section>
  );
}
